import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError, descargarRecurso } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, fmtNum } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowLeft, ArrowRight, Truck, Factory, Package, Wheat, CheckCircle2, AlertTriangle, Siren, Download, Clock, Phone } from "lucide-react";
import { toast } from "sonner";

const ESTADO_CLS = {
  disponible: "bg-[#10B981]", agotado: "bg-[#94A3B8]", bloqueado: "bg-[#EF4444]", retirado: "bg-[#0E2A47]",
};

export default function Trazabilidad() {
  const { user, scopeTenant } = useAuth();
  const canEdit = ["superadmin", "admin", "calidad", "operario"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);
  const [params, setParams] = useSearchParams();

  const [busqueda, setBusqueda] = useState(params.get("codigo") || "");
  const [arbol, setArbol] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sim, setSim] = useState(null);
  const [simulando, setSimulando] = useState(false);

  const cargarLote = useCallback(async (loteId) => {
    setLoading(true); setSim(null);
    try {
      const { data } = await api.get(`/trazabilidad/lote/${loteId}`);
      setArbol(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setArbol(null); }
    finally { setLoading(false); }
  }, []);

  const resolver = useCallback(async (codigo) => {
    if (!codigo?.trim()) return;
    setLoading(true); setSim(null);
    try {
      const { data } = await api.get(`/trazabilidad/resolver`, { params: { codigo: codigo.trim() } });
      setParams({ codigo: data.codigo_resuelto });
      await cargarLote(data.lote_id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setArbol(null); setLoading(false); }
  }, [cargarLote, setParams]);

  const [initialCodigo] = useState(() => params.get("codigo") || "");

  useEffect(() => {
    if (initialCodigo && !needsScope) resolver(initialCodigo);
  }, [initialCodigo, needsScope, resolver]);

  const irANodo = (codigo, loteId) => { setBusqueda(codigo); setParams({ codigo }); cargarLote(loteId); };

  const simular = async () => {
    setSimulando(true);
    try {
      const { data } = await api.post(`/simulacros/lote/${arbol.lote.id}`);
      setSim(data); toast.success(`Simulacro completado en ${data.duracion_segundos}s`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSimulando(false); }
  };

  if (needsScope) return <ScopeNotice />;

  const lote = arbol?.lote;

  return (
    <div data-testid="trazabilidad-page">
      <PageHeader overline="Nivel 3 · Traza" title="Trazabilidad y Simulacro" help="trazabilidad" />

      <Card className="p-4 border-[#E2E8F0] mb-6">
        <div className="flex gap-2">
          <Input placeholder="Código de lote interno, lote de proveedor o albarán…" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} onKeyDown={(e) => e.key === "Enter" && resolver(busqueda)}
            data-testid="traza-buscar-input" className="flex-1" />
          <Button onClick={() => resolver(busqueda)} data-testid="traza-buscar-btn" className="bg-[#0E2A47] hover:bg-[#163C65]"><Search size={16} className="mr-1" />Buscar</Button>
        </div>
      </Card>

      {loading && <div className="text-[#0E2A47]/50 py-8 text-center">Cargando trazabilidad…</div>}

      {!loading && arbol && lote && (
        <div data-testid="traza-arbol" data-verify-traza-cierra={arbol.traza_cierra}>
          {/* Lote origen */}
          <Card className="p-5 border-[#E2E8F0] mb-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                {lote.tipo === "producto" ? <Package className="text-[#8B5CF6]" size={24} /> : <Wheat className="text-[#F59E0B]" size={24} />}
                <div>
                  <div className="font-mono font-black text-2xl text-[#0E2A47]">{lote.codigo}</div>
                  <div className="text-sm text-[#0E2A47]/60">{lote.materia_prima_nombre || "—"} · inicial {fmtNum(lote.cantidad_inicial)} {lote.unidad} · disp. {fmtNum(lote.disponible)} · exp. {fmtNum(lote.expedida)}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {arbol.traza_cierra
                  ? <Badge className="bg-[#10B981] hover:bg-[#10B981] text-white" data-testid="traza-cierra-ok"><CheckCircle2 size={13} className="mr-1" />Trazabilidad cierra</Badge>
                  : <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-white" data-testid="traza-cierra-ko"><AlertTriangle size={13} className="mr-1" />No cierra</Badge>}
                {canEdit && <Button onClick={simular} disabled={simulando} data-testid="simular-retirada-btn" className="bg-[#EF4444] hover:bg-[#dc2626]"><Siren size={16} className="mr-1" />{simulando ? "Simulando…" : "Simular retirada"}</Button>}
              </div>
            </div>
          </Card>

          {/* Resultado simulacro */}
          {sim && (
            <Card className="p-5 border-[#EF4444]/30 bg-[#FEF2F2] mb-5" data-testid="simulacro-resultado">
              <div className="flex items-center gap-2 mb-3"><Siren size={18} className="text-[#EF4444]" /><span className="font-bold text-[#B91C1C]">Simulacro de retirada ejecutado</span></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="text-xs uppercase tracking-wider text-[#0E2A47]/50">Duración</div><div className="font-bold text-[#0E2A47] flex items-center gap-1" data-testid="sim-duracion"><Clock size={14} />{sim.duracion_segundos < 0.1 ? "<1s" : `${sim.duracion_segundos}s`}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-[#0E2A47]/50">Producto localizado</div><div className="font-bold text-[#0E2A47]">{sim.pct_localizado}%</div></div>
                <div><div className="text-xs uppercase tracking-wider text-[#0E2A47]/50">Clientes afectados</div><div className="font-bold text-[#0E2A47]">{new Set(sim.clientes_afectados.map((c) => c.cliente)).size}</div></div>
                <div className="flex items-end"><Button size="sm" variant="outline" onClick={() => descargarRecurso("simulacro", sim.id)} data-testid="sim-descargar"><Download size={14} className="mr-1" />PDF</Button></div>
              </div>
              <p className="text-xs text-[#0E2A47]/50 mt-3">IFS exige demostrar trazabilidad completa en ≤ 4 horas. Aquí: {sim.duracion_segundos < 0.1 ? "menos de 1" : sim.duracion_segundos} segundos. Evidencia inmutable guardada en Informes.</p>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Atrás */}
            <div>
              <h2 className="flex items-center gap-2 text-lg font-bold text-[#0E2A47] mb-3"><ArrowLeft size={18} className="text-[#0E2A47]/50" />Hacia atrás (origen)</h2>
              {lote.tipo === "producto" ? (
                arbol.atras.partes.length === 0 ? <p className="text-[#0E2A47]/40 text-sm">Sin partes de producción.</p> :
                arbol.atras.partes.map((pp) => (
                  <Card key={pp.parte.id} className="p-3 border-[#E2E8F0] mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Factory size={15} className="text-[#3B82F6]" />
                      <span className="font-mono font-semibold text-[#0E2A47]">{pp.parte.codigo}</span>
                      {pp.es_ajuste && <Badge variant="outline" className="text-[10px] border-[#F59E0B] text-[#B45309]">Ajuste</Badge>}
                    </div>
                    {pp.consumos.length === 0 ? <p className="text-xs text-[#0E2A47]/40">Sin consumos.</p> :
                      pp.consumos.map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-sm border-l-2 border-[#F59E0B]/40 pl-2 py-1 ml-2">
                          <button className="flex items-center gap-1.5 text-[#0E2A47] hover:text-[#FF5625]" onClick={() => c.lote_mp && irANodo(c.lote_mp.codigo, c.lote_mp.id)} data-testid={`nodo-mp-${c.lote_mp?.codigo}`}>
                            <Wheat size={13} className="text-[#F59E0B]" /><span className="font-mono">{c.lote_mp?.codigo || "—"}</span>
                          </button>
                          <span className="text-xs text-[#0E2A47]/60">{fmtNum(c.cantidad)} · {c.proveedor?.nombre || "—"}{c.lote_proveedor ? ` · lote prov. ${c.lote_proveedor}` : ""}</span>
                        </div>
                      ))}
                  </Card>
                ))
              ) : (
                <Card className="p-3 border-[#E2E8F0]">
                  <div className="text-sm text-[#0E2A47]"><b>Proveedor:</b> {arbol.atras.proveedor?.nombre || "—"}</div>
                  <div className="text-sm text-[#0E2A47] mt-1"><b>Lote de proveedor:</b> {arbol.atras.lote_proveedor || "—"}</div>
                  {arbol.atras.recepcion && <div className="text-xs text-[#0E2A47]/50 mt-1">Recepción: {new Date(arbol.atras.recepcion.fecha).toLocaleDateString("es-ES")}</div>}
                </Card>
              )}
            </div>

            {/* Adelante */}
            <div>
              <h2 className="flex items-center gap-2 text-lg font-bold text-[#0E2A47] mb-3"><ArrowRight size={18} className="text-[#0E2A47]/50" />Hacia adelante (destino)</h2>
              {lote.tipo === "mp" ? (
                arbol.adelante.productos.length === 0 ? <p className="text-[#0E2A47]/40 text-sm">Aún no transformado en producto.</p> :
                arbol.adelante.productos.map((p) => (
                  <Card key={p.lote_pt.id} className="p-3 border-[#E2E8F0] mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <button className="flex items-center gap-1.5 font-mono font-semibold text-[#0E2A47] hover:text-[#FF5625]" onClick={() => irANodo(p.lote_pt.codigo, p.lote_pt.id)} data-testid={`nodo-pt-${p.lote_pt.codigo}`}>
                        <Package size={15} className="text-[#8B5CF6]" />{p.lote_pt.codigo}
                      </button>
                      <span className="text-xs text-[#0E2A47]/60">consumido {fmtNum(p.cantidad_consumida)}</span>
                    </div>
                    <ExpList exps={p.expediciones} />
                  </Card>
                ))
              ) : (
                <Card className="p-3 border-[#E2E8F0]">
                  <ExpList exps={arbol.adelante.expediciones} />
                </Card>
              )}
            </div>
          </div>

          {arbol.contactos_clientes && arbol.contactos_clientes.length > 0 && (
            <Card className="p-4 border-[#E2E8F0] mt-4" data-testid="traza-contactos">
              <h2 className="flex items-center gap-2 text-lg font-bold text-[#0E2A47] mb-3"><Phone size={18} className="text-[#0E2A47]/50" />Contactos de clientes afectados</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {arbol.contactos_clientes.map((c, i) => (
                  <div key={i} className={`rounded-md border p-3 ${c.sin_datos ? "border-[#EF4444]/40 bg-[#FEF2F2]" : "border-[#E2E8F0]"}`} data-testid={`contacto-${i}`}>
                    <div className="font-semibold text-[#0E2A47]">{c.cliente}</div>
                    {c.sin_datos ? (
                      <div className="text-xs text-[#EF4444] mt-1 flex items-center gap-1"><AlertTriangle size={13} />Sin datos de contacto para notificación</div>
                    ) : (
                      <div className="text-xs text-[#0E2A47]/70 mt-1 space-y-0.5">
                        {c.persona_contacto && <div>{c.persona_contacto}</div>}
                        <div>{c.telefono || "—"} · {c.email || "—"}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {!loading && !arbol && (
        <div className="text-[#0E2A47]/40 py-12 text-center">Introduzca un código para ver su árbol de trazabilidad.</div>
      )}
    </div>
  );
}

function ExpList({ exps }) {
  if (!exps || exps.length === 0) return <p className="text-xs text-[#0E2A47]/40">Sin expediciones.</p>;
  return (
    <div className="space-y-1">
      {exps.map((e) => (
        <div key={e.id} className="flex items-center justify-between text-sm border-l-2 border-[#8B5CF6]/40 pl-2 py-1 ml-2" data-testid={`nodo-exp-${e.codigo}`}>
          <span className="flex items-center gap-1.5 text-[#0E2A47]"><Truck size={13} className="text-[#8B5CF6]" /><span className="font-mono">{e.codigo}</span></span>
          <span className="text-xs text-[#0E2A47]/60">{e.cliente} · {fmtNum(e.cantidad)}{e.albaran ? ` · ${e.albaran}` : ""}</span>
        </div>
      ))}
    </div>
  );
}
